import React from 'react';
import { useMatch } from 'react-router-dom';
import { Button, LinkButton } from '@sato/components';
import { observer } from 'mobx-react-lite';

import { Link } from '@components/common/Link';
import { ROUTES } from '@routes/routes.constants';
import { t } from '@stores/localization';
import { parkingRentingStore } from '@stores/parkingRentingStore/parkingRentingStore';
import { switchParkingSpacesStore } from '@stores/parkingRentingStore/switchParkingSpacesStore';

import { useAbortRentingDialog } from './useAbortRentingDialog';

/** Allows to return to or abort parking space renting or changing. Rendered only if there is an ongoing process. */
export const OngoingParkingRentingBanner = observer(() => {
  // This banner should not be visible in parking space renting form page, so check if the path matches it
  const rentingFormPageMatch = useMatch({
    path: ROUTES.PARKING_SPACE_RENTING_FORM,
  });

  const changingFormPageMatch = useMatch({
    path: ROUTES.SWITCH_PARKING_SPACES_FORM,
  });

  const rentingIdentificationCallbackPageMatch = useMatch({
    path: ROUTES.PARKING_SPACE_RENTING_IDENTIFICATION_CALLBACK_PAGE,
  });

  const rentingIdentificationMethodPageMatch = useMatch({
    path: ROUTES.PARKING_SPACE_RENTING_IDENTIFICATION_METHOD_PAGE,
  });

  const switchingIdentificationCallbackPageMatch = useMatch({
    path: ROUTES.SWITCH_PARKING_SPACES_IDENTIFICATION_CALLBACK_PAGE,
  });

  const switchingIdentificationMethodPageMatch = useMatch({
    path: ROUTES.SWITCH_PARKING_SPACES_IDENTIFICATION_METHOD_PAGE,
  });

  const store = parkingRentingStore.hasOngoingLock
    ? parkingRentingStore
    : switchParkingSpacesStore;

  const abortRentingDialog = useAbortRentingDialog({
    storeType: store === parkingRentingStore ? 'rentNewSpace' : 'switchSpaces',
  });

  if (
    !store.hasOngoingLock ||
    !!rentingFormPageMatch ||
    !!changingFormPageMatch ||
    !!rentingIdentificationCallbackPageMatch ||
    !!rentingIdentificationMethodPageMatch ||
    !!switchingIdentificationCallbackPageMatch ||
    !!switchingIdentificationMethodPageMatch
  ) {
    return null;
  }

  return (
    <div className="fixed bottom-0 left-0 right-0 z-modal flex flex-wrap justify-center gap-x-3 gap-y-2 border-t border-black-light bg-white p-2 shadow-md lg:p-3">
      <Button onClick={abortRentingDialog.open} variant="text">
        {t('parking.abortRenting')}
      </Button>

      {store === parkingRentingStore ? (
        <LinkButton<Link<typeof ROUTES.PARKING_SPACE_RENTING_FORM>>
          linkComponent={Link}
          to={ROUTES.PARKING_SPACE_RENTING_FORM}
        >
          {t('parking.continueRenting')}
        </LinkButton>
      ) : (
        <LinkButton<Link<typeof ROUTES.SWITCH_PARKING_SPACES_FORM>>
          linkComponent={Link}
          to={ROUTES.SWITCH_PARKING_SPACES_FORM}
        >
          {t('parking.continueChanging')}
        </LinkButton>
      )}
    </div>
  );
});
